import { useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client/react';
import {
  AiConversationFeatureEnum,
  AiConversationFragmentFragment,
  AiMessageFragmentFragment,
  AiMessageFragmentFragmentDoc,
  AiMessageRoleEnum,
  AiMessagesDocument,
  AiMessagesQuery,
  AiMessagesQueryVariables,
  AiMessageStatusEnum,
  AiStreamEventTypeEnum,
  AiThinkingLevelEnum,
  SortDirectionEnum,
  useAiConversationStreamSubscription,
  useAiMessagesQuery,
  useAiSendMessageMutation,
  useAiSendMessageStopMutation,
} from 'graphql-common';
import {
  AI_STREAM_TURN_INITIAL_STATE,
  AiStreamEvent,
  AiStreamTurnState,
  applyAiStreamEvent,
  getAiStreamToolSteps,
  getAiStreamRunningToolName,
  getAiStreamText,
  getAiStreamThinkingText,
  isAiStreamTerminalStatus,
  isAiStreamThinking,
} from '@lib/utils/ai/streamReducer';
import toast from '@lib/components/Toast/Toast';
import { logger } from '@lib/services/logger';
import { getAiSendErrorText } from '../utils/getAiErrorText';
import isAiTurnOutcomeRow from '../utils/isAiTurnOutcomeRow';
import parseContentBlocks, {
  AiContentBlockType,
} from '../utils/parseContentBlocks';

const MESSAGES_LIMIT = 10;

// Plain-text content of a persisted message, joined from its text content
// blocks (the BE dropped the flat `content` field) with a fallback to the
// legacy field. Used to match a persisted user message back to the optimistic
// prompt for the current turn — both here and in ConversationPane, which must
// use the SAME rule or the two disagree and the user's message renders twice.
export function getAiMessagePlainText(
  message: AiMessageFragmentFragment,
): string {
  const fromBlocks = parseContentBlocks(message.contentBlocks)
    .filter((block) => block.type === AiContentBlockType.Text)
    .map((block) => (block.type === AiContentBlockType.Text ? block.text : ''))
    .join('')
    .trim();
  return fromBlocks || (message.content ?? '').trim();
}

// Whether `messages` (ascending, oldest-first) already contains THIS turn's
// persisted user message matching `content`. The match is scoped to rows newer
// than the turn-start snapshot: a bare content match would also hit a PREVIOUS
// identical prompt and wrongly suppress the fresh optimistic bubble for the
// whole turn — re-sending the same text left the just-typed message invisible
// under the thinking spinner until the post-turn refetch. Both the optimistic-
// bubble guard (ConversationPane) and the failed-unpersisted settle below use
// this so they agree on which row is this turn's copy. `turnStartNewestId`
// undefined (a new conversation with no prior transcript) falls back to the
// whole list, which is correct there — every row belongs to this turn.
export function hasTurnUserMessage(
  messages: AiMessageFragmentFragment[],
  turnStartNewestId: string | undefined,
  content: string,
): boolean {
  const startIndex = turnStartNewestId
    ? messages.findIndex((message) => message.id === turnStartNewestId)
    : -1;
  const trimmed = content.trim();
  return messages
    .slice(startIndex + 1)
    .some(
      (message) =>
        message.role === AiMessageRoleEnum.User &&
        getAiMessagePlainText(message) === trimmed,
    );
}

// Concatenate already-ordered message lists and drop duplicate ids, keeping the
// first occurrence so the input order is preserved. We deliberately do NOT sort
// by createdAt: the BE returns each page in authoritative order, and a
// createdAt sort with an id tie-break reorders a question/answer pair that share
// the same timestamp (their UUIDs sort randomly).
function dedupMessages(
  messages: AiMessageFragmentFragment[],
): AiMessageFragmentFragment[] {
  const seen = new Set<string>();
  return messages.filter((message) => {
    if (seen.has(message.id)) return false;
    seen.add(message.id);
    return true;
  });
}

// A page comes back newest-first (desc); reverse it to oldest-first for display.
function toAscending(
  page: AiMessageFragmentFragment[],
): AiMessageFragmentFragment[] {
  return [...page].reverse();
}
// Cadence for the inactivity-watchdog poll (see the effect below). The FIRST
// check waits a full interval so a healthy stream's warm-up (or a slow tool run
// that still emits periodic frames) is never mistaken for a stall; each retry
// backs off exponentially up to a cap. A frame arriving resets the sequence, so
// these delays measure CONTINUOUS stream silence, not total turn time.
const POLL_INITIAL_DELAY_MS = 6000;
const POLL_BACKOFF_BASE_MS = 4000;
const POLL_MAX_INTERVAL_MS = 20000;
// Attempts are spaced 6s, 4s, 8s, 16s, 20s, 20s → a turn is only declared
// failed after ~74s of UNINTERRUPTED stream silence, comfortably past even a
// slow tool-heavy warm-up so a healthy turn is never false-failed.
const POLL_MAX_ATTEMPTS = 6;

// Delay before the poll attempt numbered `attempt` (0-based): the first attempt
// uses the warm-up window, each retry doubles a base delay up to a cap.
export function getAiPollDelayMs(attempt: number): number {
  if (attempt <= 0) return POLL_INITIAL_DELAY_MS;
  const backoff = POLL_BACKOFF_BASE_MS * 2 ** (attempt - 1);
  return Math.min(backoff, POLL_MAX_INTERVAL_MS);
}

type TurnAction = { type: 'event'; event: AiStreamEvent } | { type: 'reset' };

function turnReducer(
  state: AiStreamTurnState,
  action: TurnAction,
): AiStreamTurnState {
  if (action.type === 'reset') return AI_STREAM_TURN_INITIAL_STATE;
  return applyAiStreamEvent(state, action.event);
}

export type PendingUserMessage = {
  content: string;
  createdAt: string;
};

type Args = {
  // null starts a new conversation on the first sent message.
  conversationId: string | null;
  // Whether an assistant reply was already being generated on load.
  busy?: boolean;
  // The conversation is CLOSED (context window exhausted). Doubles as a terminal
  // signal for the turn that closed it — see the closed-edge effect below.
  closed?: boolean;
  // The AI feature (assistant) new conversations belong to.
  feature: AiConversationFeatureEnum;
  onConversationCreated: (conversation: AiConversationFragmentFragment) => void;
  // Sidebar data changed (title generated, message counts, busy flag).
  onConversationsChanged: () => void;
  // A turn reached its terminal state, carrying that state's status (null when
  // the turn died without one — a give-up poll). Lets the host react to the
  // OUTCOME, not just the fact that something changed: the BE enqueues title
  // generation only on the success path, so anything but Complete means no
  // title is coming for this conversation.
  onTurnSettled?: (status: AiMessageStatusEnum | null) => void;
  // Whether the viewport is currently parked at the bottom (see
  // useStickToBottom). A getter, not a reactive value, so it always reads the
  // CURRENT position at the moment a settle happens rather than one frozen at
  // render time. Gates when a settle's older-pages realign (below) may run —
  // it silently drops content, which is only invisible while the user isn't
  // looking at it. Defaults to always-pinned so callers that don't track
  // scroll position (tests) keep the original always-reset behavior.
  isPinned?: () => boolean;
};

// Owns one conversation turn: optimistic user message, ActionCable stream
// accumulation (ordered by sequence), terminal refetch, follow-up suggestions.
// Mount one instance per conversation (key the component by conversationId).
export default function useAiConversation({
  conversationId,
  busy,
  closed,
  feature,
  onConversationCreated,
  onConversationsChanged,
  onTurnSettled,
  isPinned = () => true,
}: Args) {
  const { t } = useTranslation();
  const [turn, dispatch] = useReducer(
    turnReducer,
    AI_STREAM_TURN_INITIAL_STATE,
  );
  const [pendingUserMessage, setPendingUserMessage] =
    useState<PendingUserMessage | null>(null);
  // Set once the busy-on-load turn (or a sent turn) reaches a terminal frame.
  const [turnFinished, setTurnFinished] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  // Terminal error shown inline when the BE reported a failed turn but did not
  // persist a message for it (messagesCount stays 0). Without this the failed
  // turn would silently reset to the empty state, losing the user's message.
  const [finishedTurnError, setFinishedTurnError] = useState<{
    status: AiMessageStatusEnum;
    errorCode: string | null;
  } | null>(null);

  // The newest persisted row as it stood when the current turn started. The
  // transcript-settle below compares against it to tell this turn's outcome from
  // the previous turn's answer, which is also terminal and otherwise
  // indistinguishable while the transcript is still stale.
  const turnStartNewestIdRef = useRef<string | undefined>(undefined);

  // Bumped on every sendMessage call. The composer re-enables (isGenerating
  // goes false) the instant the terminal stream frame is processed — well
  // before finishTurn's refetch below resolves — so the user can already have
  // started a NEW turn by the time an OLD finishTurn's promise settles.
  // finishTurn snapshots this at call time and compares it against the current
  // value when its refetch resolves, so a superseded resolution can tell it no
  // longer owns the live turn state and must not touch it.
  const turnTokenRef = useRef(0);

  // Per-turn frame accounting for the debug log: arrival index, sequences seen
  // (duplicate / out-of-order detection), and empty frames — makes BE-vs-FE
  // delivery gaps observable without a network inspector.
  const frameStatsRef = useRef({
    frameIndex: 0,
    emptyFrames: 0,
    seenSequences: new Set<number>(),
  });
  const resetFrameStats = () => {
    frameStatsRef.current = {
      frameIndex: 0,
      emptyFrames: 0,
      seenSequences: new Set<number>(),
    };
  };

  // Assistant message ids whose live-frame stream is retired: a turn ends
  // locally either by settling (settleTurnState/settleTurnWithError) or by
  // being abandoned via stopGeneration + an immediate new sendMessage —
  // stopGeneration only flips `turnFinished`, it does not reset the reducer,
  // so the retiring turn's messageId is still readable here right before
  // whichever path resets it. MESSAGE/THINKING/TOOL frames are keyed into the
  // reducer purely by `sequence`, which restarts at 1 every turn, so a
  // straggler from THIS turn — a redelivered frame, or one simply delayed on
  // the wire — arriving after the reset would otherwise land in the new
  // turn's frame slot with no error: latching its messageId onto the new turn
  // or prepending its delta to the new answer's text. Recording the id here
  // lets onData recognize and drop that straggler instead. Not cleared, but
  // bounded by the conversation's turn count for this mounted instance —
  // trivial memory for a chat session.
  const retiredMessageIdsRef = useRef<Set<string>>(new Set());
  const retireCurrentTurn = () => {
    if (turn.messageId) retiredMessageIdsRef.current.add(turn.messageId);
  };

  // Guards the async turn-finish refetch against resolving after the pane
  // unmounts (e.g. switching conversations mid-turn remounts it), which would
  // otherwise fire a stray conversations-list refetch on a dead instance.
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const client = useApolloClient();
  // Page 1 holds the NEWEST messages (sorted desc) so the tail of a long
  // conversation — and any message sent past the 100th — is always present.
  // Older pages are loaded on scroll-up and accumulated separately.
  const messagesQuery = useAiMessagesQuery({
    variables: {
      conversationId: conversationId as string,
      page: 1,
      limit: MESSAGES_LIMIT,
      sorts: { createdAt: SortDirectionEnum.Desc },
    },
    skip: !conversationId,
    fetchPolicy: 'cache-and-network',
  });

  // Older pages (page 2..N), fetched lazily as the user scrolls up.
  const [olderMessages, setOlderMessages] = useState<
    AiMessageFragmentFragment[]
  >([]);
  const [olderPagesLoaded, setOlderPagesLoaded] = useState(0);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // The last older-page load failed. The pane's automatic viewport fill
  // (useFillViewportWithOlderPages) retries on its own until the transcript
  // overflows, so without this a persistent failure — a 500, a dropped
  // connection — turns into an unbounded request-per-round-trip loop, each
  // iteration toasting. Cleared on any successful page and on the next
  // realign, which only happens off a user action (a turn settling, the user
  // returning to the bottom) and so paces retries to at most one per action.
  // The scroll-driven trigger is deliberately NOT gated on it: that one is a
  // user gesture, and a gesture asking again should ask again.
  const [olderPagesFailed, setOlderPagesFailed] = useState(false);
  // Set when a turn settles while older pages are loaded and the user isn't
  // pinned to the bottom (they're reading history) — see settleOlderPages
  // below for why the realign itself waits.
  const olderPagesStaleRef = useRef(false);

  const totalPages = messagesQuery.data?.data.metadata.totalPages ?? 1;
  // Page 1 is the live (newest) page; older pages run 2..totalPages.
  const hasMoreOlder = olderPagesLoaded + 1 < totalPages;

  const loadOlderMessages = () => {
    const nextPage = olderPagesLoaded + 2;
    if (loadingOlder || !conversationId || nextPage > totalPages) return;
    setLoadingOlder(true);
    logger.debug('AiAssistant: loading older messages page', {
      conversationId,
      page: nextPage,
    });
    client
      .query<AiMessagesQuery, AiMessagesQueryVariables>({
        query: AiMessagesDocument,
        variables: {
          conversationId,
          page: nextPage,
          limit: MESSAGES_LIMIT,
          sorts: { createdAt: SortDirectionEnum.Desc },
        },
        fetchPolicy: 'network-only',
      })
      .then((result) => {
        if (cancelledRef.current) return;
        const page = result.data?.data.collection ?? [];
        // This page is older than everything loaded so far → prepend (ascending).
        setOlderMessages((prev) =>
          dedupMessages([...toAscending(page), ...prev]),
        );
        setOlderPagesLoaded((count) => count + 1);
        setOlderPagesFailed(false);
      })
      .catch((error) => {
        if (cancelledRef.current) return;
        logger.error('AiAssistant: load older messages failed', {
          conversationId,
          error,
        });
        setOlderPagesFailed(true);
        toast({ content: t('ai-load-older-messages-failed') });
      })
      .finally(() => {
        if (!cancelledRef.current) setLoadingOlder(false);
      });
  };

  const [sendMessageMutation] = useAiSendMessageMutation();
  const [stopMessageMutation] = useAiSendMessageStopMutation();

  // Discard accumulated older pages so offset-paginated history realigns on the
  // next scroll-up. A turn persists 1–3 messages (user + answer, plus any
  // tool-activity rows), which shifts every desc-sorted page boundary; older
  // pages loaded before/during the turn are now off by that count and would
  // leave a gap (a dropped message) or an overlap against the refreshed page 1.
  const resetOlderPages = () => {
    setOlderMessages([]);
    setOlderPagesLoaded(0);
    olderPagesStaleRef.current = false;
    // Pagination starts over from page 2, so a previous page's failure says
    // nothing about the request that follows — and this is the retry pacing
    // for the automatic fill (see olderPagesFailed).
    setOlderPagesFailed(false);
  };

  // Settling normally coincides with the viewport being pinned to the bottom,
  // which is what makes dropping older pages invisible — but not when the user
  // has scrolled up to read history mid-turn. Resetting there yanked their
  // loaded pages out from under them (scrollTop lands wherever the now-shorter
  // content clamps it) and reset pagination back to page 1, so scrolling up
  // again could never reach further than the same point — the "stuck, can't
  // reach the first message" bug. Deferring the realign until they return to
  // the bottom (flushStaleOlderPages, called by the pane when its `pinned`
  // flips true) keeps it invisible in both cases; only deferred when there is
  // actually something loaded to disrupt.
  const settleOlderPages = () => {
    if (isPinned() || (olderPagesLoaded === 0 && olderMessages.length === 0)) {
      resetOlderPages();
    } else {
      olderPagesStaleRef.current = true;
    }
  };

  // Flush a deferred realign once the user comes back to the bottom —
  // dropping the stale pages is invisible again at that point, same as an
  // immediate reset would have been. The pane calls this from an effect keyed
  // on its own `pinned` value; a no-op when nothing was deferred.
  const flushStaleOlderPages = () => {
    if (olderPagesStaleRef.current) resetOlderPages();
  };

  // Drop the live turn state so the conversation renders entirely from the
  // (now up-to-date) persisted transcript. `status` is the turn's terminal
  // status, reported to the host via onTurnSettled.
  const settleTurnState = (status: AiMessageStatusEnum | null) => {
    retireCurrentTurn();
    dispatch({ type: 'reset' });
    setPendingUserMessage(null);
    setFinishedTurnError(null);
    setTurnFinished(true);
    settleOlderPages();
    onConversationsChanged();
    onTurnSettled?.(status);
  };

  // Settle a turn the BE failed but never persisted: drop the live stream state
  // but keep the optimistic user message and surface the error inline.
  const settleTurnWithError = (
    status: AiMessageStatusEnum,
    errorCode: string | null,
  ) => {
    retireCurrentTurn();
    dispatch({ type: 'reset' });
    setFinishedTurnError({ status, errorCode });
    setTurnFinished(true);
    settleOlderPages();
    onConversationsChanged();
    onTurnSettled?.(status);
  };

  // The terminal status/errorCode come straight from the event: finishTurn is
  // invoked synchronously after dispatch(event), before the reducer re-renders,
  // so reading `turn.status` here would be stale (the pre-terminal value).
  const finishTurn = (
    terminalStatus: AiMessageStatusEnum | null | undefined,
    terminalErrorCode: string | null | undefined,
  ) => {
    // Snapshot synchronously, before the async refetch: turnStartNewestIdRef
    // can be overwritten by a newer sendMessage before this refetch resolves,
    // so reading the ref again inside the callbacks below would silently judge
    // THIS turn's outcome against the NEXT turn's start point instead of its
    // own.
    const token = turnTokenRef.current;
    const turnStartId = turnStartNewestIdRef.current;
    logger.debug('AiAssistant: turn finished, refetching messages', {
      conversationId,
      terminalStatus,
      terminalErrorCode,
    });
    messagesQuery
      .refetch()
      .then((result) => {
        if (cancelledRef.current) return;
        // Page is sorted desc, so the newest message is the first element.
        const collection = result.data?.data.collection ?? [];
        const newest = collection[0];
        // The turn's outcome is in the transcript, so the persisted row is
        // authoritative and renders it on its own.
        const transcriptHasOutcome = isAiTurnOutcomeRow(newest, turnStartId);
        logger.debug('AiAssistant: messages refetched after turn', {
          conversationId,
          messagesCount: collection.length,
          transcriptHasOutcome,
        });
        // A newer turn already started while this refetch was in flight (see
        // turnTokenRef above) — its live state (dispatch, pendingUserMessage,
        // turnFinished, and retireCurrentTurn's turn.messageId) now belongs to
        // that turn, not this one, so settling here would clobber it. If this
        // refetch did surface THIS (superseded) turn's own outcome row, still
        // advance the shared baseline past it so the new turn's own settle
        // checks (the watchdog poll and the transcript-settle effect, both of
        // which read turnStartNewestIdRef fresh) don't mistake this row for
        // their own outcome.
        if (turnTokenRef.current !== token) {
          if (
            transcriptHasOutcome &&
            newest?.id !== turnStartNewestIdRef.current
          ) {
            turnStartNewestIdRef.current = newest?.id;
          }
          onConversationsChanged();
          onTurnSettled?.(newest?.status ?? terminalStatus ?? null);
          return;
        }
        // Defensive fallback: the BE ended the turn but the refetch came back
        // without the row (it commits before the terminal frame is broadcast, so
        // this needs a lost/replica-lagged read). Show the outcome inline rather
        // than resetting to the empty state, which would silently drop the
        // user's optimistic message. CANCELLED is a terminal status too (guide
        // §4); a server-side cancel before any message persists must not vanish
        // the bubble.
        const failedUnpersisted =
          !transcriptHasOutcome &&
          (terminalStatus === AiMessageStatusEnum.Failed ||
            terminalStatus === AiMessageStatusEnum.TruncatedBudget ||
            terminalStatus === AiMessageStatusEnum.Cancelled);
        if (failedUnpersisted) {
          logger.debug(
            'AiAssistant: failed turn not persisted, keeping inline error',
            {
              conversationId,
              status: terminalStatus,
              errorCode: terminalErrorCode,
            },
          );
          // Keep the optimistic user message unless the transcript already
          // contains THIS turn's user message, to avoid a duplicate bubble. Match
          // against the pending prompt, scoped to rows newer than the turn-start
          // snapshot — a plain content (or bare `role === User`) match hits an
          // earlier identical prompt and would wrongly vanish the just-typed
          // message in an existing conversation (which always has prior user
          // messages in the newest page). `collection` is desc; the helper wants
          // oldest-first.
          const pendingContent = pendingUserMessage?.content;
          const turnUserPersisted =
            !!pendingContent &&
            hasTurnUserMessage(
              toAscending(collection),
              turnStartNewestIdRef.current,
              pendingContent,
            );
          if (turnUserPersisted) {
            setPendingUserMessage(null);
          }
          settleTurnWithError(terminalStatus, terminalErrorCode ?? null);
        } else {
          // The persisted row is authoritative once the transcript carries the
          // outcome; fall back to the frame's status when it doesn't (a turn
          // whose outcome only ever existed on the stream).
          settleTurnState(newest?.status ?? terminalStatus ?? null);
        }
      })
      .catch((error) => {
        if (cancelledRef.current) return;
        logger.error('AiAssistant: messages refetch after turn failed', {
          conversationId,
          error,
        });
        // A newer turn already started (see the token check above) — there is
        // no fetched row to correct the baseline with, and no live state left
        // that belongs to this superseded turn, so there is nothing safe left
        // to do here.
        if (turnTokenRef.current !== token) return;
        // The terminal frame arrived but the transcript refetch failed. Don't
        // drop the outcome: on a failure status (with no persisted row to fall
        // back to) surface the inline error — degrading to the same path as an
        // unpersisted fail rather than leaving a streamed turn whose outcome is
        // never reported to the host (budget refetch, sidebar re-sort).
        if (
          terminalStatus === AiMessageStatusEnum.Failed ||
          terminalStatus === AiMessageStatusEnum.TruncatedBudget ||
          terminalStatus === AiMessageStatusEnum.Cancelled
        ) {
          settleTurnWithError(terminalStatus, terminalErrorCode ?? null);
          return;
        }
        // Success (or unknown) status: keep the streamed answer on screen since
        // the refetch that would replace it failed, but still report the settled
        // outcome so budget and the sidebar reconcile.
        setTurnFinished(true);
        onConversationsChanged();
        onTurnSettled?.(terminalStatus ?? null);
      });
  };

  useAiConversationStreamSubscription({
    variables: { conversationId: conversationId as string },
    skip: !conversationId,
    // Stream frames are transient deltas with no `id` — consumed only via
    // onData below, never read from the cache. Writing them to the normalized
    // store is pointless and throws "Missing field 'data'" on graphql-ruby's
    // empty subscription-registration frame, which tears the stream down.
    fetchPolicy: 'no-cache',
    onData: ({ data }) => {
      const event = data.data?.data;
      const stats = frameStatsRef.current;
      if (!event) {
        // graphql-ruby delivers subscription-level errors as an in-band frame
        // (`{ data: null, errors: [...] }`) that Apollo surfaces here as
        // `data.error`, NOT via the onError callback (the same quirk the
        // field-generation hook guards against). The benign empty registration
        // frame it sends on subscribe carries no error, so only an
        // error-bearing frame is a real failure — the normal first frame is
        // still counted as empty.
        if (data.error) {
          logger.error('AiAssistant: stream frame carried an error', {
            conversationId,
            error: data.error,
          });
          // Settle an in-flight turn now instead of leaving it to hang until the
          // ~74s inactivity poll gives up. A revived stream (a later content
          // frame) clears this inline error via setFinishedTurnError(null).
          if (!turnFinished && (!!pendingUserMessage || !!busy)) {
            settleTurnWithError(AiMessageStatusEnum.Failed, null);
          }
          return;
        }
        // graphql-ruby sends an empty registration frame on subscribe; anything
        // beyond one of those per subscription would mean malformed frames.
        stats.emptyFrames += 1;
        logger.debug('AiAssistant: empty stream frame', {
          conversationId,
          emptyFrames: stats.emptyFrames,
        });
        return;
      }
      // A straggler from a turn that already ended locally (see
      // retiredMessageIdsRef above): drop it before it touches frame stats,
      // the reducer, or finishTurn. Scoped to the event types that drive the
      // live turn view — TITLE/SUGGESTIONS frames legitimately arrive after
      // their turn's terminal frame for that same messageId (guide §4/§5) and
      // must still be applied.
      const isLiveTurnFrame =
        event.eventType === AiStreamEventTypeEnum.Message ||
        event.eventType === AiStreamEventTypeEnum.Thinking ||
        event.eventType === AiStreamEventTypeEnum.Tool;
      if (
        isLiveTurnFrame &&
        event.messageId &&
        retiredMessageIdsRef.current.has(event.messageId)
      ) {
        logger.warn('AiAssistant: stale frame from a retired turn dropped', {
          conversationId,
          messageId: event.messageId,
          eventType: event.eventType,
          sequence: event.sequence,
        });
        return;
      }
      stats.frameIndex += 1;
      logger.debug('AiAssistant: stream frame received', {
        conversationId,
        frameIndex: stats.frameIndex,
        eventType: event.eventType,
        messageId: event.messageId,
        sequence: event.sequence,
        status: event.status,
        deltaLength: event.delta?.length ?? 0,
        errorCode: event.errorCode,
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        toolStatus: event.toolStatus,
        suggestionsCount: event.suggestedFollowups?.length ?? 0,
        hasTitle: !!event.conversationTitle,
      });
      // Frames the reducer silently ignores — surface them as warnings so a
      // "tool never rendered" report can be traced to a concrete dropped frame.
      if (event.eventType === AiStreamEventTypeEnum.Tool && !event.toolCallId) {
        logger.warn('AiAssistant: TOOL frame without toolCallId dropped', {
          conversationId,
          toolName: event.toolName,
          toolStatus: event.toolStatus,
        });
      }
      if (typeof event.sequence === 'number' && event.sequence >= 1) {
        if (stats.seenSequences.has(event.sequence)) {
          logger.warn('AiAssistant: duplicate MESSAGE sequence dropped', {
            conversationId,
            sequence: event.sequence,
          });
        } else {
          const maxSeen = Math.max(0, ...stats.seenSequences);
          if (event.sequence < maxSeen) {
            logger.debug('AiAssistant: out-of-order MESSAGE frame', {
              conversationId,
              sequence: event.sequence,
              maxSequenceSeen: maxSeen,
            });
          }
          stats.seenSequences.add(event.sequence);
        }
      }
      setConnectionLost(false);
      // A content-bearing frame means the turn is generating after all: clear
      // any inline error a give-up poll (or an unpersisted-fail settle) has
      // already shown, so the revived stream doesn't render beneath a stale
      // "failed" bubble — which would also double-render the turn's tool steps.
      // Title/suggestions frames are excluded: those can legitimately trail a
      // finished turn and must not revive a settled error.
      if (
        event.eventType === AiStreamEventTypeEnum.Message ||
        event.eventType === AiStreamEventTypeEnum.Tool
      ) {
        setFinishedTurnError(null);
      }
      dispatch({ type: 'event', event });
      if (event.conversationTitle) {
        const title = event.conversationTitle;
        logger.debug('AiAssistant: conversation title generated', {
          conversationId,
          title,
        });
        // The title arrives on the stream as soon as the BE generates it —
        // earlier than the next conversations-list fetch. Write it straight onto
        // the cached AiConversation so the sidebar re-renders immediately (same
        // approach as suggestedFollowups below). cache.modify returns false when
        // the conversation isn't cached yet (e.g. a brand-new chat not yet in the
        // list), in which case fall back to a list refetch.
        const conversationCacheId = conversationId
          ? client.cache.identify({
              __typename: 'AiConversation',
              id: conversationId,
            })
          : undefined;
        const written =
          !!conversationCacheId &&
          client.cache.modify({
            id: conversationCacheId,
            fields: {
              title: () => title,
              titleAutoGenerated: () => true,
            },
          });
        logger.debug('AiAssistant: conversation title applied', {
          conversationId,
          title,
          appliedToCache: written,
          fallbackRefetch: !written,
        });
        if (!written) {
          onConversationsChanged();
        }
      }
      if (isAiStreamTerminalStatus(event.status)) {
        // Sequences run 1..terminal without gaps on the BE side; anything
        // missing here was published but never delivered to this client.
        const maxSequence =
          typeof event.sequence === 'number'
            ? event.sequence
            : Math.max(0, ...stats.seenSequences);
        const missingSequences: number[] = [];
        for (let sequence = 1; sequence <= maxSequence; sequence += 1) {
          if (!stats.seenSequences.has(sequence)) {
            missingSequences.push(sequence);
          }
        }
        logger.debug('AiAssistant: terminal stream status', {
          conversationId,
          status: event.status,
          errorCode: event.errorCode,
          sequence: event.sequence,
          framesReceived: stats.frameIndex,
          missingSequences,
        });
        if (missingSequences.length) {
          logger.warn('AiAssistant: MESSAGE frames never delivered', {
            conversationId,
            missingSequences,
          });
        }
        finishTurn(event.status, event.errorCode);
      }
      if (event.suggestedFollowups?.length) {
        logger.debug('AiAssistant: follow-up suggestions received', {
          conversationId,
          messageId: event.messageId,
          suggestionsCount: event.suggestedFollowups.length,
        });
        // Suggestions are generated by a separate worker after the terminal
        // frame and persisted on the message. The frame already carries them,
        // so write them straight onto the cached message — the watched
        // aiMessages query re-renders with the persisted value and no extra
        // round-trip. cache.modify returns false when the target message isn't
        // cached (e.g. the frame omitted messageId), so fall back to a refetch
        // only in that case.
        const followups = event.suggestedFollowups;
        const messageCacheId = event.messageId
          ? client.cache.identify({
              __typename: 'AiMessage',
              id: event.messageId,
            })
          : undefined;
        const written =
          !!messageCacheId &&
          client.cache.modify({
            id: messageCacheId,
            fields: { suggestedFollowups: () => followups },
          });
        if (!written) {
          messagesQuery.refetch().catch((error) => {
            // Suggestions are non-critical (worst case: none shown), but don't
            // swallow the failure silently — leave a trace if they consistently
            // fail to load.
            logger.debug('AiAssistant: suggestions refetch failed', {
              conversationId,
              error,
            });
          });
        }
      }
    },
    onError: (error) => {
      logger.error('AiAssistant: stream subscription error', {
        conversationId,
        error,
      });
      setConnectionLost(true);
      toast({ content: t('ai-connection-lost') });
    },
  });

  // Safety-net poll for turns whose terminal frame never arrives on the stream.
  // Two failure modes it covers (datizy-api/docs/fe/ai_frontend_guide.md §4 lifecycle):
  //   1. No frame ever delivered — the turn failed/completed in the gap before
  //      the ActionCable subscription was confirmed (e.g. budget_not_configured).
  //   2. Some frames delivered, then the socket dropped (laptop sleep, wifi
  //      blip, server restart) BEFORE the terminal frame — ActionCable has no
  //      replay, and a disconnect is not surfaced as an onError, so the turn
  //      would otherwise spin forever with the composer disabled.
  //
  // It runs as an INACTIVITY watchdog rather than an all-or-nothing gate: it
  // re-checks the transcript only after the stream has gone quiet for a full
  // interval, and resets the moment a new frame arrives (tracked via the
  // ever-incrementing frameStatsRef.frameIndex). So during healthy streaming —
  // and during a long tool run that still emits periodic frames — it never
  // refetches; it only wakes up when the stream genuinely stalls.
  const turnInFlight = !turnFinished && (!!pendingUserMessage || !!busy);
  useEffect(() => {
    if (!conversationId || !turnInFlight) return undefined;

    let cancelled = false;
    let attempts = 0;
    // Frame count observed at the last check; a change means the stream is
    // still alive, so the stall counter resets instead of counting toward
    // give-up. Starts at the current count so a turn that has already streamed
    // frames isn't treated as freshly stalled.
    let lastFrameIndex = frameStatsRef.current.frameIndex;
    let timer: ReturnType<typeof setTimeout>;

    function poll() {
      if (cancelled) return;
      // Helpers reference `poll` (recursion), so they live inside it — declaring
      // them before `poll` would read as use-before-define.
      const streamAdvanced = () =>
        frameStatsRef.current.frameIndex !== lastFrameIndex;
      // The stream is alive again: absorb the new frame count, drop the stall
      // counter, and wait out another full quiet interval before re-checking.
      const noteActivityAndReschedule = () => {
        lastFrameIndex = frameStatsRef.current.frameIndex;
        attempts = 0;
        timer = setTimeout(poll, getAiPollDelayMs(0));
      };

      // A frame landed during the last interval → not stalled; wait again.
      if (streamAdvanced()) {
        noteActivityAndReschedule();
        return;
      }
      attempts += 1;
      messagesQuery
        .refetch()
        .then((result) => {
          if (cancelled) return;
          // A frame arrived while the refetch was in flight — the stream is
          // live again, so don't count this as a stall or give up on it.
          if (streamAdvanced()) {
            noteActivityAndReschedule();
            return;
          }
          // Page is sorted desc, so the newest message is the first element.
          const collection = result.data?.data.collection ?? [];
          const newest = collection[0];
          const settled = isAiTurnOutcomeRow(
            newest,
            turnStartNewestIdRef.current,
          );
          if (settled) {
            logger.debug('AiAssistant: turn settled from transcript poll', {
              conversationId,
              attempts,
              status: newest.status,
            });
            settleTurnState(newest.status);
          } else if (attempts < POLL_MAX_ATTEMPTS) {
            timer = setTimeout(poll, getAiPollDelayMs(attempts));
          } else {
            logger.error('AiAssistant: turn never resolved; giving up poll', {
              conversationId,
              attempts,
            });
            settleTurnWithError(AiMessageStatusEnum.Failed, null);
          }
        })
        .catch(() => {
          if (cancelled) return;
          if (streamAdvanced()) {
            noteActivityAndReschedule();
          } else if (attempts < POLL_MAX_ATTEMPTS) {
            timer = setTimeout(poll, getAiPollDelayMs(attempts));
          } else {
            // Mirror the give-up in the `.then` branch: if every refetch
            // rejected (sustained 5xx / offline) while the stream stayed
            // silent, settle with an error instead of leaving the turn stuck
            // with a permanent spinner and a disabled composer.
            logger.error('AiAssistant: turn never resolved; giving up poll', {
              conversationId,
              attempts,
            });
            settleTurnWithError(AiMessageStatusEnum.Failed, null);
          }
        });
    }

    timer = setTimeout(poll, getAiPollDelayMs(0));
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, turnInFlight]);

  // A conversation flipping to CLOSED ends the turn that closed it.
  //
  // A narrow fallback, not the main path: the BE finalizes the turn in the same
  // transaction that closes the conversation, so the transcript-settle below
  // normally reports the outcome first and this effect's guard returns. It only
  // does work when the two fetches straddle that commit — the aiMessages fetch
  // resolving just before it, the conversations-list fetch just after — leaving
  // `closed` as the only thing on screen that knows the turn is over.
  //
  // The BE closes a conversation in exactly ONE place — the context-overflow
  // rescue in Ai::SendMessageWorker — which in the same transaction finalizes
  // the turn as TRUNCATED_BUDGET / conversation_too_long, hence those arguments.
  // Settling is always safe: sends to a closed conversation are rejected
  // (Ai::Conversations::FindOrCreateOperation) and the status is never un-set,
  // so a closed conversation can never have a live turn. Routing through
  // finishTurn rather than settling directly keeps the partial-answer case
  // right — a turn that streamed text before overflowing persists that text, so
  // its row is in the transcript and wins over the inline error.
  const prevClosedRef = useRef(closed);
  useEffect(() => {
    const justClosed = !prevClosedRef.current && !!closed;
    prevClosedRef.current = closed;
    if (!justClosed || !turnInFlight) return;
    logger.debug('AiAssistant: conversation closed, settling in-flight turn', {
      conversationId,
    });
    finishTurn(AiMessageStatusEnum.TruncatedBudget, 'conversation_too_long');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closed, turnInFlight]);

  // `thinkingLevel` sets the conversation's reasoning level from this message
  // on. It is STICKY conversation state on the BE (new conversations now start
  // EXTENDED). Currently every caller omits it — the composer's picker is
  // hidden (see AiAssistant/README.md) — so the BE's sticky default applies
  // and a withheld level (e.g. STANDARD) is never sent, which the BE would now
  // reject with a validation error rather than silently ignore.
  const sendMessage = (
    content: string,
    thinkingLevel?: AiThinkingLevelEnum,
  ) => {
    const trimmed = content.trim();
    if (!trimmed) return;
    logger.debug('AiAssistant: sending message', {
      conversationId,
      feature,
      contentLength: trimmed.length,
      isNewConversation: !conversationId,
      thinkingLevel,
    });
    // Invalidates any earlier finishTurn call still awaiting its refetch (the
    // composer re-enables before that refetch resolves — see turnTokenRef
    // above), so its resolution recognizes it no longer owns the live turn
    // state below and leaves it alone.
    turnTokenRef.current += 1;
    setPendingUserMessage({
      content: trimmed,
      createdAt: new Date().toISOString(),
    });
    // Snapshot the newest row (the live page is sorted desc) before the BE
    // persists anything for this turn, so the transcript-settle below doesn't
    // mistake the previous turn's answer for this turn's outcome. Undefined for a
    // new conversation, which has no transcript to confuse it with.
    turnStartNewestIdRef.current = messagesQuery.data?.data.collection[0]?.id;
    // Drop accumulated older pages: sending grows the total message count, which
    // shifts offset-paginated page boundaries and would leave a gap/overlap in
    // scrolled-back history. They reload aligned if the user scrolls up again.
    setOlderMessages([]);
    setOlderPagesLoaded(0);
    setTurnFinished(false);
    setFinishedTurnError(null);
    // A prior subscription error latches connectionLost true, which forces
    // isGenerating false. Clear it here so this fresh turn shows the thinking
    // bubble / Stop button and disables the composer, instead of leaving it
    // enabled for overlapping sends because a past turn's socket dropped.
    setConnectionLost(false);
    retireCurrentTurn();
    dispatch({ type: 'reset' });
    resetFrameStats();
    sendMessageMutation({
      variables: {
        attributes: {
          // The message is now an ordered list of content blocks (the BE dropped
          // the flat `content` field). The composer only produces plain text, so
          // a message is a single text block.
          contentBlocks: [{ text: trimmed }],
          feature,
          conversationId: conversationId ?? undefined,
          // Sticky: sets the conversation's level from here on. Omitted keeps the
          // current level; a level the active model doesn't support is ignored
          // server-side, so it's always safe to send the picker's value.
          thinkingLevel,
        },
      },
    })
      .then(({ data }) => {
        const conversation = data?.data;
        logger.debug('AiAssistant: message sent', {
          conversationId: conversation?.id,
          busy: conversation?.busy,
          messagesCount: conversation?.messagesCount,
        });
        if (!conversationId && conversation) {
          logger.debug('AiAssistant: new conversation created', {
            conversationId: conversation.id,
          });
          // onConversationCreated already refetches the list (see modal).
          onConversationCreated(conversation);
        } else {
          // Existing conversation: the send bumps its `updatedAt`, so refetch
          // the list to re-sort and bubble this chat to the top immediately —
          // rather than only once the turn finishes, which can be 20-30s later
          // while the answer streams.
          onConversationsChanged();
        }
      })
      .catch((error) => {
        logger.error('AiAssistant: send message failed', {
          conversationId,
          feature,
          error,
        });
        setPendingUserMessage(null);
        toast({ content: getAiSendErrorText(t, error) });
      });
  };

  // Stop the in-flight turn. Two parts: (a) fire the server-side stop mutation so
  // the BE actually halts generation (and stops billing) instead of finishing the
  // turn in the background; (b) settle the turn locally right away for instant
  // feedback — the composer swaps the stop button back to send and the partial
  // streamed answer stays on screen. The mutation returns immediately; the real
  // cancellation lands as the turn's terminal aiConversationStream frame (status
  // CANCELLED, errorCode stopped_by_user), reconciled by the existing terminal
  // handling. Firing is optimistic and idempotent — repeat calls are server-side
  // no-ops (guide §4).
  const stopGeneration = () => {
    logger.debug('AiAssistant: user stopped generation', { conversationId });
    setTurnFinished(true);
    // A turn always belongs to an existing conversation; guard anyway so a brand-
    // new conversation still mid-creation falls back to the local-only settle.
    if (!conversationId) return;
    stopMessageMutation({ variables: { id: conversationId } }).catch(
      (error) => {
        // Non-fatal: the turn is already settled locally and the stream/poll
        // delivers the terminal frame regardless. Just log (e.g. a rare
        // AI_STOP_GENERATION_RATE_LIMITED from spamming the button).
        logger.error('AiAssistant: stop generation failed', {
          conversationId,
          error,
        });
      },
    );
  };

  // Live (newest) page in desc order; combine with accumulated older pages into
  // a single ascending list so the rest of the component reads oldest→newest
  // with the newest message last. Order comes straight from the BE pages.
  const livePage = messagesQuery.data?.data.collection ?? [];
  // Older pages are stored as one-shot `client.query` snapshots (not a watched
  // query), so they don't react to later cache writes on their own. Re-read each
  // from the normalized cache on render so updates to an AiMessage entity — e.g.
  // like/dislike feedback applied to a scrolled-back message — are reflected;
  // fall back to the snapshot if the entity was evicted.
  const freshOlderMessages = olderMessages.map(
    (message) =>
      client.readFragment<AiMessageFragmentFragment>({
        id: client.cache.identify(message),
        fragment: AiMessageFragmentFragmentDoc,
        fragmentName: 'aiMessageFragment',
      }) ?? message,
  );
  const messages = dedupMessages([
    ...freshOlderMessages,
    ...toAscending(livePage),
  ]);
  // Whether the transcript already carries this turn's outcome (`messages` is
  // ascending, so its last row is the newest). Besides driving the settle below,
  // this is what rescues a stale `busy` flag — a turn that finished between the
  // conversations-list snapshot and load has no terminal frame coming, and would
  // otherwise leave the composer disabled forever.
  const lastMessage = messages[messages.length - 1];
  const transcriptSettled = isAiTurnOutcomeRow(
    lastMessage,
    turnStartNewestIdRef.current,
  );

  // Settle as soon as the transcript carries the outcome, whichever fetch brought
  // it in. The stream's terminal frame normally reports it first, but on a NEW
  // conversation a turn that fails instantly publishes that frame before the
  // ActionCable subscription is confirmed, and it is lost for good (no replay) —
  // which leaves the conversation's first aiMessages fetch, ~1s in, as the only
  // thing that knows. Without this the poll above is the next to notice, 6s
  // later, and until then a Thinking bubble and a Stop button sit underneath the
  // error the transcript has already rendered. Settling from `messages` is what
  // keeps those two honest: the bubble and the spinner now read the same signal,
  // so they cannot contradict each other.
  useEffect(() => {
    if (!turnInFlight || !transcriptSettled) return;
    logger.debug('AiAssistant: turn settled from transcript', {
      conversationId,
      status: lastMessage.status,
    });
    settleTurnState(lastMessage.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnInFlight, transcriptSettled]);

  const turnText = getAiStreamText(turn);
  // Live reasoning accumulated from THINKING frames — the "Thoughts" panel's
  // source while the turn streams; on reload it comes from the persisted
  // message's `thinking` field instead (guide §4/§5).
  const turnThinkingText = getAiStreamThinkingText(turn);
  const turnActive =
    !turnFinished &&
    (!!pendingUserMessage ||
      turn.maxSequence > 0 ||
      (!!busy && !transcriptSettled));
  const isGenerating =
    turnActive && !isAiStreamTerminalStatus(turn.status) && !connectionLost;

  return {
    messages,
    // Whether the transcript already carries this turn's outcome, and so renders
    // it on its own. The settle below acts on it, but a render needs it too: it
    // is the handover point between the live turn state and the persisted rows,
    // and anything drawn from the stream has to step aside exactly here — a
    // frame earlier leaves a gap, a frame later double-renders the outcome.
    transcriptSettled,
    loadingMessages: messagesQuery.loading && !messagesQuery.data,
    // Id of the newest transcript row when this turn started, so the pane can
    // scope its optimistic-bubble dedup to messages persisted during this turn.
    turnStartNewestId: turnStartNewestIdRef.current,
    hasMoreOlder,
    loadingOlder,
    // The last older-page request failed; the pane's automatic fill stands
    // down on it so a persistent failure cannot become a retry loop.
    olderPagesFailed,
    loadOlderMessages,
    // Call from an effect keyed on the pane's `pinned` value once it flips
    // true — see settleOlderPages/flushStaleOlderPages above.
    flushStaleOlderPages,
    pendingUserMessage,
    turnText,
    turnThinkingText,
    turnMessageId: turn.messageId,
    turnStatus: turn.status,
    turnErrorCode: turn.errorCode,
    turnSuggestions: turn.suggestedFollowups,
    isThinking: isGenerating && isAiStreamThinking(turn),
    runningToolName: isGenerating ? getAiStreamRunningToolName(turn) : null,
    // Includes the running tool (rendered as an in-progress step), not just the
    // completed ones. Not gated on isGenerating: the steps must stay on screen
    // after the terminal frame too, until the refetch swaps in the persisted
    // rows. `settleTurnState` resets the turn, so they never linger.
    turnToolSteps: getAiStreamToolSteps(turn),
    isGenerating,
    finishedTurnError,
    sendMessage,
    stopGeneration,
  };
}
